import { createSelector } from 'reselect';

import type { RootState } from 'config/store/reducers';
import type { Vehicle } from 'modules/entity/subEntities/intervention/types';

import { PROVIEW } from 'constants/Parameters';
import { MEDIA_TYPES, SIGNATURE_STATUS } from 'constants/Global';
import { isAppointment } from 'constants/EntityConstants';
import { isMobile as isMobileSelector } from 'modules/app/selectors';
import { isSales, isStock } from 'modules/entity/subEntities/intervention/selectors';
import { canDisplayMessages as canDisplayMessagesSelector } from 'modules/chat/selectors';
import { canDisplayWorksForm } from 'modules/entity/subEntities/intervention/selectors/works';
import { getControlReportTireScans } from 'modules/entity/subEntities/intervention/selectors/controlReport';
import { getKeyDepositDocuments as getAppointmentDocuments } from 'modules/entity/subEntities/appointment/selectors';
import { isLayoutWorks, isToBeSent as isToBeSentSelector } from 'modules/entity/subEntities/intervention/selectors/general';

import { getContact, getDealer, getDefaultLanguage, getEntity, getLanguage, isPGA as isPGASelector } from '.';
import { getI18nMessage } from '../../../utils/utils';

const getProps = (_: unknown, props?: unknown) => props;

const getAllSignableDocuments = createSelector(
  getEntity,
  getLanguage,
  getDefaultLanguage,
  ({ documents = [] }, language, dealerLanguage) =>
    documents.map(document => ({
      id: document.id,
      type: document.media?.type,
      filename: document.media?.filename,
      path: document.media?.path,
      name: getI18nMessage(language, dealerLanguage, document.name) ?? document.media?.filename,
      status: document.status,
      signatureBox: document.signatureBox,
      isSignable: true,
    }))
);

export const getUnsignedDocuments = createSelector(
  getAllSignableDocuments,
  documents => documents.filter(document => document.status === SIGNATURE_STATUS.UNSIGNED)
);

export const getSignUploadUrl = createSelector(
  getEntity,
  ({ signUploadUrl }) => signUploadUrl
);

export const getSignDocumentUrl = createSelector(
  getEntity,
  ({ signDocumentUrl }) => signDocumentUrl
);

export const getDocumentSignatureLoading = (state: RootState) => state.entity.ui.documentSignatureLoading;

const getSignedDocuments = createSelector(
  getAllSignableDocuments,
  documents => documents.filter(document => document.status !== SIGNATURE_STATUS.UNSIGNED)
);

export const getDocuments = createSelector(
  getEntity,
  getControlReportTireScans,
  getAppointmentDocuments,
  getSignedDocuments,
  getProps,
  // eslint-disable-next-line default-param-last
  ({ medias = [] }, tireScans = [], appointmentDocuments = [], signedDocuments = [], intl) => [
    ...(medias.filter(({ type }) => type === MEDIA_TYPES.DOCUMENT)),
    ...appointmentDocuments,
    ...tireScans.map((tireScan) => ({
      id: tireScan.originTireScanId,
      path: tireScan.reportUrl,
      type: MEDIA_TYPES.DOCUMENT,
      isTireScan: true,
      name: intl?.formatMessage({
        id: 'web.documents.tscan',
        defaultMessage: 'Tire Diagnostics',
      }),
    })),
    ...signedDocuments,
  ]
);

export const hasDocuments = createSelector(
  getEntity,
  getDocuments,
  getUnsignedDocuments,
  ({ offerUrl }, documents, unsignedDocuments) => documents.length > 0 || Boolean(offerUrl) || unsignedDocuments.length > 0
);

const hasRating = createSelector(
  getEntity,
  getDealer,
  ({ rateUrl, rating }, { allowSalesRating }) => rateUrl || rating || allowSalesRating
);

export const getVehicle = createSelector(getEntity, ({ vehicleInfo }): Vehicle => vehicleInfo as Vehicle);

export const getPageContact = createSelector(
  getEntity,
  getContact,
  isStock,
  ({ author, sentBy }, contact, isStockFile) => (isStockFile ? contact : (author || sentBy))
);

export const canDownloadZip = createSelector(getEntity, ({ mediasZipUrl }) => Boolean(PROVIEW && mediasZipUrl));
export const shouldShowSidebarMessages = createSelector(
  isPGASelector,
  isToBeSentSelector,
  canDisplayMessagesSelector,
  isMobileSelector,
  (isPGA, isToBeSent, canDisplayMessages, isMobile) =>
    !isPGA && (!isToBeSent || isAppointment) && canDisplayMessages && !isMobile
);

export const shouldShowSidebarPGAWorks = createSelector(
  isPGASelector,
  canDisplayWorksForm,
  isLayoutWorks,
  (isPGA, shouldShowWorks, isWorks) => isPGA && shouldShowWorks && !isAppointment && isWorks
);

export const shouldShowSidebarVehicle = createSelector(
  getVehicle,
  isPGASelector,
  (vehicleInfo, isPGA) => vehicleInfo && !isPGA
);

export const shouldShowSidebarOpinion = createSelector(
  getEntity,
  isSales,
  isStock,
  hasRating,
  ({ opinion, opinionUrl }, isSalesFile, isStockFile, canRate) =>
    (opinionUrl || opinion) && !PROVIEW && isSalesFile && !isStockFile && !canRate
);
